import type { RecordOfMatchesState } from '../matches/types';
import type { RecordOfStagesState, SSRStagesState } from './types';

import type { BackendEntities } from '../types/backendEntities';
import {
  fillStateWithMatchIds,
  removeMatches,
  transformNodeSSRState,
  updateField,
} from '../utils';
import { createStageEntity, initialState } from './records';
import { transformStage } from './transform';

// ---

// Transform the `entities.tournamentStages` node's server-side rendered state.
export function transformStagesSSRState(state: SSRStagesState): RecordOfStagesState {
  return transformNodeSSRState(state, initialState, (entry) => {
    return createStageEntity({
      id: entry.id,
      name: entry.name,
      sportId: entry.sportId,
      tournamentUnionId: entry.tournamentUnionId,
      tournamentId: entry.tournamentId,
      matchesCount: entry.matchesCount,
      priority: typeof entry.priority === 'number' ? entry.priority : 0,
      promotionLevel: typeof entry.promotionLevel === 'number' ? entry.promotionLevel : 0,
      matchIds: new Set(entry.matchIds),
    });
  });
}

// Tournament stage entities reducer.
function tournamentStagesReducer(
  state: RecordOfStagesState = initialState(),
  clientEntities: BackendEntities | undefined,
  matchesState: RecordOfMatchesState['byId'] | undefined,
): RecordOfStagesState {
  if (!clientEntities?.diff) {
    return state;
  }

  const {
    tournamentStages,
    matches,
    tournamentStageMatchesCount,
    removedMatches,
  } = clientEntities.diff;

  if (!(tournamentStages || matches || tournamentStageMatchesCount || removedMatches)) {
    return state;
  }

  let tournamentStagesState = state.get('byId').asMutable();
  let tournamentStageIds = state.get('allIds');

  if (removedMatches && matchesState) {
    const newState = removeMatches(
      'tournamentStageId',
      removedMatches,
      matchesState,
      tournamentStagesState,
      tournamentStageIds,
    );

    tournamentStagesState = newState.state;
    tournamentStageIds = newState.stateIds;
  }

  // .asMutable() - i did it here, because the .delete() function works in Immutable.List() only in immutable mode
  tournamentStageIds = tournamentStageIds.asMutable();

  if (tournamentStages) {
    const tournamentStagesKeys = Object.keys(tournamentStages);

    tournamentStagesKeys.forEach((tournamentStageKey) => {
      const transformed = transformStage(
        tournamentStages[tournamentStageKey],
      );

      if (tournamentStagesState.has(transformed.id)) {
        if (transformed.name !== undefined) {
          tournamentStagesState = updateField(tournamentStagesState, transformed.id, 'name', transformed.name);
        }
        if (transformed.priority !== undefined) {
          tournamentStagesState = updateField(tournamentStagesState, transformed.id, 'priority', transformed.priority);
        }
        if (transformed.promotionLevel !== undefined) {
          tournamentStagesState = updateField(tournamentStagesState, transformed.id, 'promotionLevel', transformed.promotionLevel);
        }
      }
      else if (matches || tournamentStageMatchesCount) {
        tournamentStageIds = tournamentStageIds.push(transformed.id);
        tournamentStagesState = tournamentStagesState.set(
          transformed.id,
          createStageEntity(transformed),
        );
      }
    });
  }

  if (matches || tournamentStageMatchesCount) {
    const newState = fillStateWithMatchIds(
      'tournamentStageId',
      matches,
      tournamentStageMatchesCount,
      tournamentStagesState,
      tournamentStageIds,
    );

    tournamentStagesState = newState.state;
    tournamentStageIds = newState.stateIds;
  }

  state = state.set('byId', tournamentStagesState.asImmutable());
  state = state.set('allIds', tournamentStageIds.asImmutable());

  return state;
}

export default tournamentStagesReducer;
